/**
 * Normalize a single bookDraft error entry into a flat string that preserves
 * BOTH the API error code and the human-readable message, so that downstream
 * code-based dictionary lookups (parseErrorCodesToMessages) can match by code.
 *
 * Input shapes observed from the API:
 *  - plain string: `"Some error text"`
 *  - JSON-encoded string: `'{"code":"API_SER_RULES_001","message":"..."}'`
 *  - object: `{ code: "API_SER_RULES_001", message: "..." }`
 *  - text-prefixed JSON string: `"Error processing rate plan: {...JSON containing code...}"`
 *    (in this case JSON.parse fails and we return the raw string, which still
 *    contains the code as a substring for downstream matchers)
 */
export function formatBookDraftErrorEntry(entry: any): string {
  if (entry == null) return ''
  if (typeof entry === 'string') {
    try {
      const parsed = JSON.parse(entry)
      if (parsed && typeof parsed === 'object') {
        if (parsed.code && parsed.message) return `${parsed.code}: ${parsed.message}`
        if (parsed.message) return String(parsed.message)
        if (parsed.code) return String(parsed.code)
      }
    } catch (_) {
      // Not JSON; fall through and return raw string
    }
    return entry
  }
  if (typeof entry === 'object') {
    if (entry.code && entry.message) return `${entry.code}: ${entry.message}`
    if (entry.message) return String(entry.message)
    if (entry.code) return String(entry.code)
    return JSON.stringify(entry)
  }
  return String(entry)
}

export function formatBookDraftErrors(errors: any[] | null | undefined): string {
  if (!Array.isArray(errors)) return ''
  return errors
    .map(formatBookDraftErrorEntry)
    .filter((s) => s && s.length > 0)
    .join(', ')
}
